// The pure half of the move list: where the panel goes for a given canvas
// and viewport, and what cells a game's moves make. No DOM.
import type { SanPly } from '../game/pgn.js';

/** Beside the board, under it as a strip, or nowhere when neither fits. */
export type MoveListPlacement =
  | { readonly mode: 'side'; readonly left: number; readonly top: number; readonly width: number; readonly height: number }
  | { readonly mode: 'strip'; readonly left: number; readonly top: number; readonly width: number; readonly maxHeight: number }
  | { readonly mode: 'hidden' };

export interface LayoutInputs {
  /** The canvas's box on screen. */
  readonly canvas: { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number };
  readonly viewportWidth: number;
  readonly viewportHeight: number;
  /** Height taken at the bottom of the viewport by something else (the replay bar). */
  readonly bottomInset: number;
  /** One rem in px: the thresholds follow the player's font size. */
  readonly remPx: number;
}

const GAP_REM = 0.75;
/** Narrower than this and a side panel cannot show "12. Nxe5+ Qxe5" on one row. */
const SIDE_MIN_WIDTH_REM = 10;
const SIDE_MAX_WIDTH_REM = 16;
/** One row of moves plus its padding. */
const STRIP_MIN_HEIGHT_REM = 2.5;

export function placeMoveList(i: LayoutInputs): MoveListPlacement {
  const gap = GAP_REM * i.remPx;
  const floor = i.viewportHeight - i.bottomInset;
  const sideRoom = i.viewportWidth - i.canvas.right - 2 * gap;
  if (sideRoom >= SIDE_MIN_WIDTH_REM * i.remPx) {
    return {
      mode: 'side',
      left: i.canvas.right + gap,
      top: i.canvas.top,
      width: Math.min(sideRoom, SIDE_MAX_WIDTH_REM * i.remPx),
      height: Math.min(i.canvas.bottom, floor) - i.canvas.top,
    };
  }
  const below = floor - i.canvas.bottom;
  if (below >= STRIP_MIN_HEIGHT_REM * i.remPx) {
    return { mode: 'strip', left: i.canvas.left, top: i.canvas.bottom, width: i.canvas.right - i.canvas.left, maxHeight: below };
  }
  return { mode: 'hidden' };
}

export type MoveCell =
  | { readonly kind: 'number'; readonly text: string }
  /** Keeps Black's first move in Black's column when a game starts with Black to move. */
  | { readonly kind: 'gap' }
  | { readonly kind: 'move'; readonly ply: number; readonly san: string; readonly label: string };

/** The list as a flat run of cells: "1." "e4" "e5" "2." … */
export function moveCells(plies: readonly SanPly[]): MoveCell[] {
  const cells: MoveCell[] = [];
  plies.forEach((ply, index) => {
    const blackFirst = index === 0 && ply.turn === 'b';
    if (ply.turn === 'w') cells.push({ kind: 'number', text: `${ply.fullmoveNumber}.` });
    else if (blackFirst) cells.push({ kind: 'number', text: `${ply.fullmoveNumber}.` }, { kind: 'gap' });
    const label = `${ply.fullmoveNumber}.${ply.turn === 'b' ? '..' : ''} ${ply.san}`;
    cells.push({ kind: 'move', ply: index, san: ply.san, label });
  });
  return cells;
}
